import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";

const months = [
  "Січень",
  "Лютий",
  "Березень",
  "Квітень",
  "Травень",
  "Червень",
  "Липень",
  "Серпень",
  "Вересень",
  "Жовтень",
  "Листопад",
  "Грудень",
];

const EarningsModal = ({ open, onClose }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const url = `/finance/payments/?month=${String(month).padStart(
    2,
    "0"
  )}&year=${year}`;

  const { data, loading, error } = useFetchWithAuth(url, {}, [year, month]);

  const handleEsc = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, handleEsc]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handlePrevMonth = () => {
    setMonth((prev) => {
      if (prev === 1) {
        setYear((y) => y - 1);
        return 12;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setMonth((prev) => {
      if (prev === 12) {
        setYear((y) => y + 1);
        return 1;
      }
      return prev + 1;
    });
  };

  const payments = data?.payments || [];
  const total = data?.total || 0;

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        background: "rgba(0,0,0,0.4)",
      }}
    >
      <Box
        sx={{
          background: "var(--whiteColor, #FFFFFF)",
          color: "var(--blackColor, #000000)",
          ".dark-theme &": {
            background: "#101522",
            color: "var(--whiteColor, #FFFFFF)",
          },
          borderRadius: "20px",
          p: 4,
          minWidth: 927,
          width: 827,
          mb: 2,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              mb: 2,
              ".dark-theme &": { color: "var(--whiteColor, #FFFFFF)" },
            }}
          >
            Виплати за місяць
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "var(--blackColor, #000000)",
              position: "absolute",
              top: 24,
              right: 24,
              ".dark-theme &": { color: "var(--whiteColor, #FFFFFF)" },
            }}
          >
            <span style={{ fontSize: 28, fontWeight: 300 }}>&times;</span>
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "var(--modalIconBg, #F3F4F6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                ".dark-theme &": { background: "#19233A" },
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 25 29"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.212 8.66672V20.3334C22.212 20.8681 22.0216 21.3259 21.6408 21.7067C21.2601 22.0874 20.8023 22.2778 20.2676 22.2778H4.71202C4.1773 22.2778 3.71955 22.0874 3.33876 21.7067C2.95797 21.3259 2.76758 20.8681 2.76758 20.3334V8.66672C2.76758 8.132 2.95797 7.67424 3.33876 7.29346C3.71955 6.91267 4.1773 6.72228 4.71202 6.72228H20.2676C20.8023 6.72228 21.2601 6.91267 21.6408 7.29346C22.0216 7.67424 22.212 8.132 22.212 8.66672ZM4.71202 10.6112H20.2676V8.66672H4.71202V10.6112ZM4.71202 14.5001V20.3334H20.2676V14.5001H4.71202Z"
                  fill="#605DFF"
                />
              </svg>
            </Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 22,
                ml: 1,
              }}
            >
              Загально:{" "}
              <span
                style={{
                  color: total >= 0 ? "#22C55E" : "#EF4444",
                  fontWeight: 700,
                  fontSize: 22,
                  marginLeft: 8,
                }}
              >
                {total >= 0 ? `+${total}₴` : `${total}₴`}
              </span>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--modalIconBg, #F3F4F6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                ".dark-theme &": { background: "#19233A" },
              }}
              onClick={handlePrevMonth}
            >
              <ArrowBackIosNewIcon />
            </Box>

            <Typography sx={{ fontWeight: 500, fontSize: 22, mx: 2 }}>
              {months[month - 1]} {year}
            </Typography>

            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--modalIconBg, #F3F4F6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                ".dark-theme &": { background: "#19233A" },
              }}
              onClick={handleNextMonth}
            >
              <ArrowForwardIosIcon />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Таблиця */}
      <Box
        sx={{
          background: "var(--whiteColor)",
          color: "var(--blackColor)",
          borderRadius: "20px",
          p: "20px",
          minWidth: 927,
          width: 827,
          overflow: "hidden",
          boxShadow: "0 2px 16px 0 rgba(0,0,0,0.04)",
          border: "1px solid var(--borderColor, #E5E7EB)",
          ".dark-theme &": {
            background: "#101522",
            color: "var(--whiteColor)",
            border: "1px solid #232B3E",
          },
        }}
      >
        <Table
          sx={{ minWidth: 787, borderCollapse: "separate", borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow>
              {[
                "Період",
                "Години",
                "Сума допу",
                "% від допу",
                "Сума ЗП",
                "Оцінка",
              ].map((col, i) => (
                <TableCell
                  className="border-bottom"
                  key={i}
                  sx={{ fontSize: 20, fontWeight: 400 }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {error ? (
              <TableRow>
                <TableCell
                  className="border-bottom"
                  colSpan={6}
                  align="center"
                  sx={{ py: 3 }}
                >
                  Помилка: {error.message}
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell
                  className="border-bottom"
                  colSpan={6}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : payments.length === 0 ? (
              <TableRow>
                <TableCell
                  className="border-bottom"
                  colSpan={6}
                  align="center"
                  sx={{ py: 3, fontSize: 18 }}
                >
                  Немає даних
                </TableCell>
              </TableRow>
            ) : (
              payments.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.date}
                  </TableCell>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.hours}
                  </TableCell>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.addsum}
                  </TableCell>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.percent}
                  </TableCell>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.amount}
                  </TableCell>
                  <TableCell className="border-bottom" sx={{ fontSize: 19 }}>
                    {row.rate}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default EarningsModal;
